import { useState } from "react";
import { rebsortenListe, rebsortenListe2 } from "../kern/fachlogik.js";
import { fmtZahl, parseZahl } from "../kern/format.js";
import { NEU, TYPEN } from "../kern/konstanten.js";
import { Auswahl, Eingabe, Feld, ModalFuss, SuchAuswahl, TextKnopf, Textbereich, VorschlagEingabe } from "../ui/primitive.jsx";
import { t } from "../i18n/index.js";


/** Eingebettete Minimalfelder für eine neue Region — wird in Winzer-
    und Weinformular wiederverwendet, damit Objekte ohne Kontextwechsel
    angelegt werden können. */
function RegionSubForm({ nr, setNr, vorschlaege }) {
  const setze = (k) => (e) => setNr({ ...nr, [k]: e.target.value });
  return (
    <div className="wl-subform voll">
      <span className="wl-subformtitel">{t("Neue Region")}</span>
      <div className="wl-formgitter">
        <Feld label={t("Name")}><Eingabe value={nr.name} onChange={setze("name")} placeholder="z. B. Priorat" /></Feld>
        <Feld label={t("Land")}><VorschlagEingabe vorschlaege={vorschlaege ? vorschlaege.land : []} value={nr.land} onChange={setze("land")} placeholder="z. B. Spanien" /></Feld>
        <Feld label={t("Breitengrad")}><Eingabe value={nr.lat} onChange={setze("lat")} inputMode="decimal" placeholder="z. B. 41,25" /></Feld>
        <Feld label={t("Längengrad")}><Eingabe value={nr.lon} onChange={setze("lon")} inputMode="decimal" placeholder="z. B. 0,85" /></Feld>
      </div>
    </div>
  );
}


function prufeNeueRegion(nr) {
  if (!nr.name.trim()) return "Bitte einen Namen für die neue Region angeben.";
  const lat = parseZahl(nr.lat), lon = parseZahl(nr.lon);
  if (lat == null || lon == null || Math.abs(lat) > 90 || Math.abs(lon) > 180)
    return "Bitte gültige Koordinaten für die neue Region angeben (Dezimalgrad).";
  return null;
}


function baueNeueRegion(nr) {
  return {
    name: nr.name.trim(), land: nr.land.trim(),
    lat: parseZahl(nr.lat), lon: parseZahl(nr.lon),
    rebsorten: "", beschreibung: "",
  };
}


function RegionForm({ objekt, vorwahlLand, vorschlaege, onSave, onCancel }) {
  const [w, setW] = useState({
    name: objekt?.name ?? "", land: objekt?.land ?? vorwahlLand ?? "",
    lat: objekt?.lat ?? "", lon: objekt?.lon ?? "",
    rebsorten: objekt?.rebsorten ?? "", beschreibung: objekt?.beschreibung ?? "",
  });
  const [fehler, setFehler] = useState(null);
  const setze = (k) => (e) => setW({ ...w, [k]: e.target.value });

  const speichern = () => {
    if (!w.name.trim()) return setFehler("Bitte einen Namen angeben.");
    const lat = parseZahl(w.lat), lon = parseZahl(w.lon);
    if (lat == null || lon == null || Math.abs(lat) > 90 || Math.abs(lon) > 180)
      return setFehler("Bitte gültige Koordinaten in Dezimalgrad angeben.");
    const err = onSave({
      name: w.name.trim(), land: w.land.trim(), lat, lon,
      rebsorten: w.rebsorten.trim(), beschreibung: w.beschreibung.trim(),
    });
    if (err) setFehler(err);
  };

  return (
    <>
      <div className="wl-formgitter">
        <Feld label={t("Name")}><Eingabe value={w.name} onChange={setze("name")} autoFocus /></Feld>
        <Feld label={t("Land")}><VorschlagEingabe vorschlaege={vorschlaege ? vorschlaege.land : []} value={w.land} onChange={setze("land")} /></Feld>
        <Feld label={t("Breitengrad")}><Eingabe value={w.lat} onChange={setze("lat")} inputMode="decimal" placeholder="z. B. 49,916" /></Feld>
        <Feld label={t("Längengrad")}><Eingabe value={w.lon} onChange={setze("lon")} inputMode="decimal" placeholder="z. B. 7,066" /></Feld>
        <Feld label={t("Wichtige Rebsorten")} breit><Eingabe value={w.rebsorten} onChange={setze("rebsorten")} /></Feld>
        <Feld label={t("Kurzbeschreibung")} breit><Textbereich value={w.beschreibung} onChange={setze("beschreibung")} /></Feld>
      </div>
      {fehler && <p className="wl-fehler">{fehler}</p>}
      <ModalFuss onCancel={onCancel} onSave={speichern} />
    </>
  );
}


function WinzerForm({ objekt, regionen, vorschlaege, onSave, onCancel }) {
  const [w, setW] = useState({
    name: objekt?.name ?? "",
    regionId: objekt?.regionId ?? (regionen[0]?.id ?? NEU),
    ort: objekt?.ort ?? "", beschreibung: objekt?.beschreibung ?? "",
  });
  const [nr, setNr] = useState({ name: "", land: "", lat: "", lon: "" });
  const [fehler, setFehler] = useState(null);
  const setze = (k) => (e) => setW({ ...w, [k]: e.target.value });
  const istNeueRegion = w.regionId === NEU;

  const speichern = () => {
    if (!w.name.trim()) return setFehler("Bitte einen Namen angeben.");
    let neueRegion = null;
    if (istNeueRegion) {
      const err = prufeNeueRegion(nr);
      if (err) return setFehler(err);
      neueRegion = baueNeueRegion(nr);
    }
    const err = onSave(
      { name: w.name.trim(), regionId: w.regionId, ort: w.ort.trim(), beschreibung: w.beschreibung.trim() },
      neueRegion,
    );
    if (err) setFehler(err);
  };

  return (
    <>
      <div className="wl-formgitter">
        <Feld label={t("Name")}><Eingabe value={w.name} onChange={setze("name")} autoFocus /></Feld>
        <Feld label={t("Region")}>
          <SuchAuswahl value={w.regionId} onChange={setze("regionId")}
            platzhalter={t("Region suchen …")}
            optionen={[
              { wert: NEU, label: "+ Neue Region anlegen …", betont: true },
              ...regionen.map((r) => ({ wert: r.id, label: r.name, neben: r.land || "" })),
            ]} />
        </Feld>
        {istNeueRegion && <RegionSubForm nr={nr} setNr={setNr} vorschlaege={vorschlaege} />}
        <Feld label={t("Ort")} breit><VorschlagEingabe vorschlaege={vorschlaege ? vorschlaege.ort : []} value={w.ort} onChange={setze("ort")} /></Feld>
        <Feld label={t("Kurzbeschreibung")} breit><Textbereich value={w.beschreibung} onChange={setze("beschreibung")} /></Feld>
      </div>
      {fehler && <p className="wl-fehler">{fehler}</p>}
      <ModalFuss onCancel={onCancel} onSave={speichern} />
    </>
  );
}


function WeinForm({ objekt, winzerListe, regionen, vorwahlWinzerId, vorschlaege, onSave, onCancel }) {
  const winzerRegion = (wid) => {
    const wz = winzerListe.find((x) => x.id === wid);
    return wz ? wz.regionId : "";
  };
  const [w, setW] = useState(() => {
    const winzerId = objekt?.winzerId ?? vorwahlWinzerId ?? (winzerListe[0]?.id ?? NEU);
    return {
      name: objekt?.name ?? "",
      winzerId,
      typ: objekt?.typ ?? "rot",
      /* Rebsorten strukturiert: [{ name, anteil }] mit String-Feldern
         im Formular; mindestens eine (leere) Zeile für die Eingabe. */
      rebsorten: (() => {
        const liste = objekt ? rebsortenListe2(objekt) : [];
        return liste.length
          ? liste.map((r) => ({ name: r.name, anteil: r.anteil != null ? String(r.anteil) : "" }))
          : [{ name: "", anteil: "" }];
      })(),
      /* Region am Wein: beim Bearbeiten die gespeicherte, sonst die
         des Winzers als Vorbelegung. "" = automatisch (Winzerregion). */
      regionId: objekt?.regionId ?? "",
      appellation: objekt?.appellation ?? "",
      klassifikation: objekt?.klassifikation ?? "",
      boden: objekt?.boden ?? "",
      /* Eigenes Label — bleibt im eigenen Kellerbuch und wandert
         nicht in den gemeinsamen Katalog. */
      label: objekt?.label ?? "",
      regionManuell: !!(objekt && objekt.regionId),
    };
  });
  /* Eingebetteter neuer Winzer — inklusive der Möglichkeit, darin
     wiederum eine neue Region anzulegen (zweistufige Einbettung). */
  const [nw, setNw] = useState({ name: "", ort: "", regionId: regionen[0]?.id ?? NEU });
  const [nr, setNr] = useState({ name: "", land: "", lat: "", lon: "" });
  const [fehler, setFehler] = useState(null);
  const setze = (k) => (e) => setW({ ...w, [k]: e.target.value });
  const setzeWinzer = (e) => {
    const winzerId = e.target.value;
    setW((v) => ({ ...v, winzerId, regionId: v.regionManuell ? v.regionId : "" }));
  };
  const setzeRegion = (e) => {
    const val = e.target.value;
    setW((v) => ({ ...v, regionId: val, regionManuell: val !== "" }));
  };
  const setzeNw = (k) => (e) => setNw({ ...nw, [k]: e.target.value });
  const setzeReb = (i, feld) => (e) => setW((v) => ({
    ...v, rebsorten: v.rebsorten.map((r, j) => j === i ? { ...r, [feld]: e.target.value } : r),
  }));
  const rebHinzu = () => setW((v) => ({ ...v, rebsorten: [...v.rebsorten, { name: "", anteil: "" }] }));
  const rebWeg = (i) => setW((v) => ({ ...v, rebsorten: v.rebsorten.length > 1 ? v.rebsorten.filter((_, j) => j !== i) : v.rebsorten }));
  const istNeuerWinzer = w.winzerId === NEU;
  const istNeueRegion = istNeuerWinzer && nw.regionId === NEU;

  const speichern = () => {
    if (!w.name.trim()) return setFehler("Bitte einen Namen angeben.");
    const rl = [];
    for (const r of w.rebsorten) {
      const nm = r.name.trim();
      if (!nm) continue;
      let anteil = null;
      if (String(r.anteil).trim() !== "") {
        anteil = parseZahl(r.anteil);
        if (anteil == null || anteil < 0 || anteil > 100)
          return setFehler(`Anteil von „${nm}" muss zwischen 0 und 100 liegen.`);
      }
      rl.push({ name: nm, anteil });
    }
    const anteilSumme = rl.reduce((s2, r) => s2 + (r.anteil || 0), 0);
    if (rl.length > 0 && rl.every((r) => r.anteil != null) && Math.round(anteilSumme) !== 100)
      return setFehler(`Die Rebsortenanteile ergeben ${fmtZahl(anteilSumme, 0)} % statt 100 %.`);
    const anzeige = rl.length
      ? (rl.every((r) => r.anteil != null) ? rl.map((r) => `${r.name} ${fmtZahl(r.anteil, 0)} %`) : rl.map((r) => r.name)).join(" · ")
      : "";
    const rebsortenPaket = { rebsortenListe: rl, rebsorten: anzeige };
    let neuerWinzer = null, neueRegion = null;
    if (istNeuerWinzer) {
      if (!nw.name.trim()) return setFehler("Bitte einen Namen für den neuen Winzer angeben.");
      if (istNeueRegion) {
        const err = prufeNeueRegion(nr);
        if (err) return setFehler(err);
        neueRegion = baueNeueRegion(nr);
      }
      neuerWinzer = { name: nw.name.trim(), ort: nw.ort.trim(), regionId: nw.regionId, beschreibung: "" };
    }
    const err = onSave(
      {
        name: w.name.trim(), winzerId: w.winzerId, typ: w.typ,
        ...rebsortenPaket,
        regionId: w.regionManuell && w.regionId ? w.regionId : null,
        appellation: w.appellation.trim(),
        klassifikation: w.klassifikation.trim(),
        boden: w.boden.trim(),
        label: w.label.trim(),
      },
      neuerWinzer, neueRegion,
    );
    if (err) setFehler(err);
  };

  return (
    <>
      <div className="wl-formgitter">
        <Feld label={t("Name")} breit><Eingabe value={w.name} onChange={setze("name")} autoFocus /></Feld>
        <Feld label={t("Winzer")}>
          <SuchAuswahl value={w.winzerId} onChange={setzeWinzer}
            platzhalter={t("Winzer suchen …")}
            optionen={[
              { wert: NEU, label: "+ Neuen Winzer anlegen …", betont: true },
              ...winzerListe.map((x) => {
                const r = regionen.find((y) => y.id === x.regionId);
                const neben = [x.ort, r ? r.name : null].filter(Boolean).join(" · ");
                return { wert: x.id, label: x.name, neben };
              }),
            ]} />
        </Feld>
        <Feld label={t("Weintyp")}>
          <Auswahl value={w.typ} onChange={setze("typ")}>
            {Object.entries(TYPEN).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}
          </Auswahl>
        </Feld>
        {!istNeuerWinzer && (
          <Feld label={t("Region")}>
            <SuchAuswahl value={w.regionManuell ? w.regionId : ""} onChange={setzeRegion}
              platzhalter={t("Region suchen …")}
              optionen={[
                {
                  wert: "",
                  label: (() => {
                    const r = regionen.find((x) => x.id === winzerRegion(w.winzerId));
                    return r ? `Automatisch — ${r.name} (Winzer)` : "Automatisch (Region des Winzers)";
                  })(),
                  betont: true,
                },
                ...regionen.map((r) => ({ wert: r.id, label: r.name, neben: r.land || "" })),
              ]} />
          </Feld>
        )}
        <Feld label={t("Appellation")}>
          <VorschlagEingabe vorschlaege={vorschlaege ? vorschlaege.appellation : []} value={w.appellation} onChange={setze("appellation")} placeholder="z. B. Saint-Aubin 1er Cru" />
        </Feld>
        <Feld label={t("Klassifikation")}>
          <VorschlagEingabe vorschlaege={vorschlaege ? vorschlaege.klassifikation : []} value={w.klassifikation} onChange={setze("klassifikation")} placeholder="z. B. 1er Cru, VDP.Grosse Lage, DOCG" />
        </Feld>
        <Feld label={t("Boden")} breit>
          <Eingabe value={w.boden} onChange={setze("boden")} placeholder="z. B. Kalkmergel mit hohem Aktivkalkanteil" />
        </Feld>
        <Feld label={t("Label")} breit>
          <VorschlagEingabe vorschlaege={vorschlaege ? vorschlaege.label : []} value={w.label}
            onChange={setze("label")} placeholder="z. B. New Wave Chardonnay" />
        </Feld>
        {istNeuerWinzer && (
          <div className="wl-subform voll">
            <span className="wl-subformtitel">{t("Neuer Winzer")}</span>
            <div className="wl-formgitter">
              <Feld label={t("Name")}><Eingabe value={nw.name} onChange={setzeNw("name")} placeholder="z. B. Clos Mogador" /></Feld>
              <Feld label={t("Ort")}><VorschlagEingabe vorschlaege={vorschlaege ? vorschlaege.ort : []} value={nw.ort} onChange={setzeNw("ort")} /></Feld>
              <Feld label={t("Region")} breit>
                <SuchAuswahl value={nw.regionId} onChange={setzeNw("regionId")}
                  platzhalter={t("Region suchen …")}
                  optionen={[
                    { wert: NEU, label: "+ Neue Region anlegen …", betont: true },
                    ...regionen.map((r) => ({ wert: r.id, label: r.name, neben: r.land || "" })),
                  ]} />
              </Feld>
              {istNeueRegion && <RegionSubForm nr={nr} setNr={setNr} vorschlaege={vorschlaege} />}
            </div>
          </div>
        )}
        <div className="wl-feld voll">
          <span className="wl-feldname">{t("Rebsorten")}<span className="wl-feldhint">— Anteil optional, in Summe 100 %</span></span>
          <div className="wl-rebliste">
            {w.rebsorten.map((r, i) => (
              <div className="wl-rebzeile" key={i}>
                <VorschlagEingabe className="wl-rebname" vorschlaege={vorschlaege ? vorschlaege.rebsorte : []} value={r.name} onChange={setzeReb(i, "name")} placeholder="z. B. Spätburgunder" />
                <span className="wl-rebanteil">
                  <Eingabe value={r.anteil} onChange={setzeReb(i, "anteil")} inputMode="decimal" placeholder="—" aria-label={t("Anteil in Prozent")} />
                  <span className="wl-rebprozent">%</span>
                </span>
                <button type="button" className="wl-rebweg" onClick={() => rebWeg(i)}
                  disabled={w.rebsorten.length === 1} aria-label={t("Rebsorte entfernen")}>×</button>
              </div>
            ))}
          </div>
          <TextKnopf className="ist-dezent" onClick={rebHinzu}>{t("+ Weitere Rebsorte")}</TextKnopf>
        </div>
      </div>
      {fehler && <p className="wl-fehler">{fehler}</p>}
      <ModalFuss onCancel={onCancel} onSave={speichern} />
    </>
  );
}

export { RegionForm, RegionSubForm, WeinForm, WinzerForm, baueNeueRegion, prufeNeueRegion };
